import IconMessageSquare from "@src/assets/icons/lucide/message-square.svg?react";
import IconHelp from "@src/assets/icons/tabler/help.svg?react";
import IconMail from "@src/assets/icons/tabler/mail.svg?react";
import IconTrash from "@src/assets/icons/tabler/trash.svg?react";
import IconX from "@src/assets/icons/tabler/x.svg?react";
import { ROUTES } from "@src/common/constants";
import { toggleAgentChatDrawer } from "@src/components/agent-chat/agent-chat-drawer-controls";
import { useMyPlan } from "@src/components/plan/queries/fetch-plan";
import { TerminalsNavBarEntry } from "@src/components/terminal/terminals-navbar-entry";
import { NativeLikeTooltip as Tooltip } from "@src/components/ui/native-like-tooltip";
import { useResendVerificationEmailAction } from "@src/components/user/mutations/use-resend-verification-email-action";
import { VERIFICATION_BANNER_DISMISSED_KEY } from "@src/components/verification/constants";
import { PlanFamily, PlanStatus, SupportedPlan } from "@src/gql/graphql";
import { useEmailVerificationStatus } from "@src/hooks/use-email-verification-status";
import clsx from "clsx";
import { AnimatePresence } from "framer-motion";
import { useState } from "react";
import { NavLink, useMatch } from "react-router-dom";
import { UpgradePopup } from "../plan/upgrade-popup";
import { ReferralNavIcon } from "../referral/referral-nav-icon";
import { NavBarMenu } from "./nav-bar-menu/nav-bar-menu";

function VerificationNotice() {
	const { needsEmailVerification } = useEmailVerificationStatus();
	const { handleResend, isPending } = useResendVerificationEmailAction();
	const [dismissed, setDismissed] = useState(
		() => sessionStorage.getItem(VERIFICATION_BANNER_DISMISSED_KEY) === "true",
	);

	if (!needsEmailVerification || dismissed) return null;

	return (
		<div className="w-full px-3">
			<div
				className={clsx(
					"w-full h-8 px-2 rounded-lg flex items-center",
					"justify-between gap-1.5",
					"bg-warning-soft border border-warning/35 shadow-xs",
					"text-text-primary",
				)}
			>
				<button
					type="button"
					onClick={handleResend}
					disabled={isPending}
					className={clsx(
						"group flex items-center gap-1.5 min-w-0 flex-1",
						"cursor-pointer text-left disabled:cursor-default",
					)}
				>
					<div
						className={clsx(
							"flex items-center justify-center size-5",
							"rounded-md shrink-0 bg-warning text-white shadow-xs",
						)}
					>
						<IconMail className="size-3 text-white" />
					</div>

					<span className="text-xs font-semibold text-text-primary truncate">
						Verify your email
					</span>

					<span className="text-xs font-semibold text-warning shrink-0 ml-auto group-hover:opacity-80">
						{isPending ? "Sending…" : "Resend"}
					</span>
				</button>

				<button
					type="button"
					onClick={() => {
						sessionStorage.setItem(VERIFICATION_BANNER_DISMISSED_KEY, "true");
						setDismissed(true);
					}}
					className={clsx(
						"flex size-5 shrink-0 items-center justify-center",
						"text-text-muted hover:text-text-primary",
					)}
					aria-label="Dismiss"
				>
					<IconX className="size-3.5" />
				</button>
			</div>
		</div>
	);
}

export function NavBarFooter() {
	const { data } = useMyPlan();
	const isTrashActive = useMatch(ROUTES.trash) != null;
	const isAskHome = useMatch(ROUTES.home) != null;
	const isElectron = Boolean(window.electronAPI?.isElectron);

	const isPaidActive =
		(data?.planFamily === PlanFamily.Pro ||
			data?.planFamily === PlanFamily.Team) &&
		data?.status === PlanStatus.Active;
	const isBeliever = data?.plan === SupportedPlan.Believer;
	const showUpgradeSection = !isPaidActive && !isBeliever;

	return (
		<div className="relative mt-auto flex flex-col gap-1 pb-1">
			{showUpgradeSection && (
				<AnimatePresence>
					<UpgradePopup />
				</AnimatePresence>
			)}

			<VerificationNotice />

			<div className="flex items-center gap-1 border-t border-border-subtle px-3">
				<ReferralNavIcon />

				{isAskHome ? null : (
					<Tooltip label="Agent chat" position="top">
						<button
							type="button"
							onClick={() => toggleAgentChatDrawer("agent")}
							aria-label="Open agent chat"
							className={clsx(
								"flex shrink-0 items-center justify-center",
								"size-8 rounded-lg leading-none cursor-pointer",
								"text-text-secondary hover:text-text-primary",
								"hover:bg-surface-hover-subtle transition-colors",
							)}
						>
							<IconMessageSquare className="size-4 shrink-0" aria-hidden />
						</button>
					</Tooltip>
				)}

				{isElectron ? <TerminalsNavBarEntry compact /> : null}

				<NavBarMenu
					tooltip="Help & Resources"
					target={
						<button
							type="button"
							aria-label="Help & Resources"
							className={clsx(
								"flex shrink-0 items-center justify-center",
								"size-8 rounded-lg leading-none cursor-pointer",
								"text-text-secondary hover:text-text-primary",
								"hover:bg-surface-hover-subtle transition-colors",
							)}
						>
							<IconHelp className="size-4 shrink-0" aria-hidden />
						</button>
					}
				/>

				<Tooltip label="Trash" position="top">
					<NavLink
						to={ROUTES.trash}
						aria-label="Trash"
						className={clsx(
							"flex shrink-0 items-center justify-center",
							"size-8 rounded-lg leading-none cursor-pointer",
							"transition-colors ml-auto",
							isTrashActive
								? "bg-interaction-selected text-brand-primary"
								: clsx(
										"text-text-secondary hover:text-text-primary",
										"hover:bg-surface-hover-subtle",
									),
						)}
					>
						<IconTrash className="size-4 shrink-0" aria-hidden />
					</NavLink>
				</Tooltip>
			</div>
		</div>
	);
}
